/**
 * features/teachers/TeacherProfile.tsx
 * ------------------------------------------------------------------
 * Round 5 redesign — unified teacher edit surface.
 *
 * Four tabs:
 *   1. Overview        — identity, contact, department, qualifications
 *   2. Teaching        — subject × class assignment matrix + class-teacher
 *                        and assistant-class-teacher dropdowns. EDITABLE.
 *   3. Performance     — per-subject pass rates and averages (lazy-loaded)
 *   4. Administrative  — staff ID, employment, dateJoined, notes
 *
 * Reached via #/teacherProfile/<teacherId>. Inline edit toggle on
 * Overview / Administrative; Teaching tab is editable when the user
 * has the canManageTeachers capability. Saves directly to Firestore
 * via the teacherAssignments helpers (two-sided sync).
 *
 * Permission: canManageTeachers (admin, systemAdmin, headteacher,
 * academicAdmin). The HR & Pay tab stays gated separately by
 * canAccessHR / canAccessPayroll (unchanged).
 * ------------------------------------------------------------------
 */
import { useState, useMemo, useEffect } from 'react';
import {
  doc,
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
// Batch A: undefined-safe write + save modal
import { updateDoc } from '@/lib/firebase';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import {
  ArrowLeft,
  Mail,
  Phone,
  Building2,
  GraduationCap,
  BookOpen,
  Users,
  Briefcase,
  IdCard,
  Pencil,
  Save,
  X,
  Check,
  AlertCircle,
  BarChart3,
  Wallet,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import TeacherHrPay from '@/features/teachers/TeacherHrPay';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/primitives';
import { stripUndefined } from '@/lib/sanitize';
import {
  setClassTeacher,
  setAssistantClassTeacher,
  saveTeachingAssignments,
} from '@/lib/teacherAssignments';
import { teacherTeachesSubjectInClass } from '@/lib/teacherLink';
import {
  deriveMark,
  getFormLevel,
  getPassMark,
} from '@/lib/grading';
import type { MarksDocument } from '@/types';

type ProfileTab = 'overview' | 'teaching' | 'performance' | 'admin' | 'hrpay';

interface EditableFields {
  title: string;
  initials: string;
  lastName: string;
  email: string;
  phone: string;
  department: string;
  qualifications: string;
  staffId: string;
  employmentStatus: string;
  dateJoined: string;
  notes: string;
}

export default function TeacherProfile({
  teacherId,
  onBack,
  year = '2026',
  term = 'Term1',
}: {
  teacherId: string;
  onBack: () => void;
  year?: string;
  term?: string;
}) {
  const { teachers, students, subjects, classes } = useData();
  const { userData, permissions } = useAuth();
  const [tab, setTab] = useState<ProfileTab>('overview');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [saveModal, setSaveModal] = useState<{
    show: boolean;
    status: 'saving' | 'success' | 'error';
    message: string;
  }>({ show: false, status: 'saving', message: '' });
  const [form, setForm] = useState<EditableFields>({
    title: '', initials: '', lastName: '', email: '',
    phone: '', department: '', qualifications: '',
    staffId: '', employmentStatus: '', dateJoined: '', notes: '',
  });

  const teacher = useMemo(
    () => teachers.find((t) => t.id === teacherId) ?? null,
    [teachers, teacherId],
  );

  const canEdit = permissions.canManageTeachers;

  // Hydrate form when teacher loads or edit mode toggles
  useEffect(() => {
    if (!teacher) return;
    setForm({
      title: teacher.title ?? '',
      initials: teacher.initials ?? '',
      lastName: teacher.lastName ?? '',
      email: teacher.email ?? '',
      phone: teacher.phone ?? '',
      department: teacher.department ?? '',
      qualifications: teacher.qualifications ?? '',
      staffId: teacher.staffId ?? '',
      employmentStatus: teacher.employmentStatus ?? '',
      dateJoined: teacher.dateJoined ?? '',
      notes: teacher.notes ?? '',
    });
  }, [teacher, editing]);

  if (!teacher) {
    return (
      <div className="p-6">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 mb-4 hover:text-gray-700">
          <ArrowLeft size={14} /> Back
        </button>
        <Card className="p-8 text-center">
          <p className="text-gray-500">Teacher not found.</p>
        </Card>
      </div>
    );
  }

  const initials = (teacher.fullName || '?')
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  /* ─── derived: which subjects + classes this teacher teaches ─── */
  const taughtSubjects = useMemo(() => {
    if (!teacher.classAssignments) return [];
    return Object.keys(teacher.classAssignments)
      .map((sid) => {
        const sub = subjects.find((s) => s.id === sid);
        const classIds = teacher.classAssignments![sid] ?? [];
        return {
          subjectId: sid,
          subjectName: sub?.name ?? sid,
          classIds,
          classNames: classIds
            .map((cid) => classes.find((c) => c.id === cid)?.name ?? cid)
            .filter(Boolean),
        };
      })
      .sort((a, b) => a.subjectName.localeCompare(b.subjectName));
  }, [teacher, subjects, classes]);

  /* ─── students this teacher teaches (across all subjects) ─── */
  const studentCount = useMemo(() => {
    if (!teacher.classAssignments) return 0;
    const ids = new Set<string>();
    for (const [subjectId, classIds] of Object.entries(teacher.classAssignments)) {
      for (const cid of classIds) {
        students
          .filter((s) => s.currentClass === cid && s.subjectTeachers?.[subjectId] === teacher.id)
          .forEach((s) => ids.add(s.id));
      }
    }
    return ids.size;
  }, [teacher, students]);

  /* ─── class teacher of: list students ─── */
  const classObj = teacher.classTeacherOf
    ? classes.find((c) => c.id === teacher.classTeacherOf)
    : null;
  const classStudents = useMemo(() => {
    if (!teacher.classTeacherOf) return [];
    return students
      .filter((s) => s.currentClass === teacher.classTeacherOf)
      .sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [teacher, students]);

  /* ─── performance stats (lazy: only when tab=performance) ─── */
  const [perfMarkDocs, setPerfMarkDocs] = useState<MarksDocument[]>([]);
  const [perfLoading, setPerfLoading] = useState(false);

  const teacherClassIds = useMemo(() => {
    if (!teacher.classAssignments) return [];
    const ids = new Set<string>();
    Object.values(teacher.classAssignments).forEach((cids) =>
      cids.forEach((c) => ids.add(c)),
    );
    return [...ids];
  }, [teacher]);

  useEffect(() => {
    if (tab !== 'performance') return;
    if (teacherClassIds.length === 0) {
      setPerfMarkDocs([]);
      return;
    }
    let cancelled = false;
    setPerfLoading(true);
    (async () => {
      try {
        const allDocs: MarksDocument[] = [];
        for (let i = 0; i < teacherClassIds.length; i += 30) {
          const chunk = teacherClassIds.slice(i, i + 30);
          const snap = await getDocs(
            query(
              collection(db, COLLECTIONS.marks),
              where('year', '==', year),
              where('term', '==', term),
              where('classId', 'in', chunk),
            ),
          );
          snap.docs.forEach((d) => allDocs.push(d.data() as MarksDocument));
        }
        if (!cancelled) setPerfMarkDocs(allDocs);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setPerfLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, teacherClassIds, year, term]);

  const perfStats = useMemo(() => {
    if (!teacher.classAssignments || perfMarkDocs.length === 0) return [];
    const results: Array<{
      subjectName: string;
      className: string;
      studentsGraded: number;
      average: number | null;
      passRate: number | null;
      highest: number | null;
      lowest: number | null;
    }> = [];

    for (const [subjectId, classIds] of Object.entries(teacher.classAssignments)) {
      const sub = subjects.find((s) => s.id === subjectId);
      for (const cid of classIds) {
        const cls = classes.find((c) => c.id === cid);
        const formLvl = getFormLevel(cls?.name ?? '') ?? '';
        const pm = getPassMark(formLvl);
        const markDoc = perfMarkDocs.find(
          (d) => d.subjectId === subjectId && d.classId === cid,
        );
        if (!markDoc) continue;

        const marks: number[] = [];
        const classStudentIds = students.filter(
          (s) => s.currentClass === cid && s.subjectTeachers?.[subjectId] === teacher.id,
        );
        for (const stu of classStudentIds) {
          const entry = markDoc.entries?.[stu.id];
          if (!entry) continue;
          // Numeric-only: reads dm.termMark (scheme-independent), so the
          // default extended scheme is fine even for Core subjects.
          const dm = deriveMark(term, entry.ca, entry.exam);
          if (dm.termMark != null && typeof dm.termMark === 'number') {
            marks.push(dm.termMark);
          }
        }
        if (marks.length === 0) continue;
        const avg = Math.round(marks.reduce((a, b) => a + b, 0) / marks.length);
        const passed = marks.filter((m) => m >= pm).length;
        results.push({
          subjectName: sub?.name ?? subjectId,
          className: cls?.name ?? cid,
          studentsGraded: marks.length,
          average: avg,
          passRate: Math.round((passed / marks.length) * 100),
          highest: Math.max(...marks),
          lowest: Math.min(...marks),
        });
      }
    }
    return results.sort(
      (a, b) =>
        a.subjectName.localeCompare(b.subjectName) ||
        a.className.localeCompare(b.className),
    );
  }, [teacher, perfMarkDocs, students, subjects, classes, term]);

  /* ─── save handlers ─── */
  async function saveOverviewOrAdmin() {
    setSaving(true);
    setMsg(null);
    setSaveModal({ show: true, status: 'saving', message: 'Saving teacher details…' });
    try {
      const initialsClean = form.initials.toUpperCase().replace(/[^A-Z]/g, '');
      const fullName = `${initialsClean} ${form.lastName.trim()}`.trim();
      const displayName = `${form.title} ${form.lastName.trim()}`.trim();
      await updateDoc(
        doc(db, COLLECTIONS.teachers, teacher!.id),
        stripUndefined({
          title: form.title,
          initials: initialsClean,
          lastName: form.lastName.trim(),
          fullName,
          displayName,
          phone: form.phone.trim() || undefined,
          department: form.department.trim() || undefined,
          qualifications: form.qualifications.trim() || undefined,
          staffId: form.staffId.trim() || undefined,
          employmentStatus: form.employmentStatus || undefined,
          dateJoined: form.dateJoined || undefined,
          notes: form.notes.trim() || undefined,
          updatedAt: new Date().toISOString(),
          updatedBy: userData?.uid,
        }),
      );
      setSaveModal({ show: true, status: 'success', message: 'Teacher details saved.' });
      setEditing(false);
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  /* ─── Teaching-tab handlers ─── */
  async function toggleSubjectClass(subjectId: string, classId: string) {
    if (!canEdit) return;
    const t = teacher!;
    // GLITCH FIX: a subject assigned at the FORM level (teachingAssignments)
    // with no explicit class list is "taught" in every class at that form via
    // the resolver's fallback — but the matrix only edited classAssignments, so
    // those links were invisible and impossible to remove (a single class could
    // never be dropped). Expand the effective assignment (what the resolver
    // actually links) into explicit class ids first, so this toggle operates on
    // the real, editable list. saveTeachingAssignments re-derives the form-level
    // mirror from it.
    const hasClassLevel = (t.classAssignments?.[subjectId]?.length ?? 0) > 0;
    const current = hasClassLevel
      ? t.classAssignments![subjectId]
      : classes
          .filter((c) => teacherTeachesSubjectInClass(t, subjectId, c.id, c.name))
          .map((c) => c.id);
    const next = current.includes(classId)
      ? current.filter((c) => c !== classId)
      : [...current, classId];
    const updated = { ...(t.classAssignments ?? {}), [subjectId]: next };
    try {
      await saveTeachingAssignments(teacher!.id, updated, { classes }, userData?.uid);
      setMsg({ ok: true, text: 'Teaching assignments updated.' });
      setTimeout(() => setMsg(null), 1500);
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    }
  }

  async function handleClassTeacherChange(classId: string) {
    if (!canEdit) return;
    setSaving(true);
    setMsg(null);
    try {
      // First unassign this teacher from whatever class they currently
      // hold (if any), then assign them to the new class.
      if (teacher!.classTeacherOf && teacher!.classTeacherOf !== classId) {
        await setClassTeacher(
          teacher!.classTeacherOf,
          null,
          { classes, teachers },
          userData?.uid,
        );
      }
      if (classId) {
        await setClassTeacher(classId, teacher!.id, { classes, teachers }, userData?.uid);
      }
      setMsg({ ok: true, text: 'Class-teacher assignment updated.' });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  async function toggleAssistantOf(classId: string) {
    if (!canEdit) return;
    const cls = classes.find((c) => c.id === classId);
    if (!cls) return;
    const currentlyAssistantHere = cls.assistantClassTeacherId === teacher!.id;
    setSaving(true);
    setMsg(null);
    try {
      await setAssistantClassTeacher(
        classId,
        currentlyAssistantHere ? null : teacher!.id,
        { classes, teachers },
        userData?.uid,
      );
      setMsg({
        ok: true,
        text: currentlyAssistantHere
          ? `Removed as assistant of ${cls.name}.`
          : `Set as assistant of ${cls.name}.`,
      });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  /* ─── small render helpers ─── */
  function Field({ label, field, placeholder, type = 'text', readOnly = false, hint }: { label: string; field: keyof EditableFields; placeholder?: string; type?: string; readOnly?: boolean; hint?: string }) {
    const v = form[field] ?? '';
    if (!editing || readOnly) {
      return (
        <div className="py-2 grid grid-cols-3 gap-2 text-sm border-b border-gray-100 dark:border-gray-700 last:border-0">
          <span className="text-gray-500">{label}</span>
          <span className="col-span-2 text-gray-800 dark:text-white">
            {v || '—'}
            {readOnly && editing && hint && (
              <span className="block text-[11px] text-gray-400 mt-0.5">{hint}</span>
            )}
          </span>
        </div>
      );
    }
    return (
      <div className="py-2 grid grid-cols-3 gap-2 items-center text-sm border-b border-gray-100 dark:border-gray-700 last:border-0">
        <label className="text-gray-500">{label}</label>
        <input
          type={type}
          value={v}
          onChange={(e) => setForm({ ...form, [field]: e.target.value })}
          placeholder={placeholder}
          className="col-span-2 px-2 py-1 rounded border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
        />
      </div>
    );
  }

  function SelectField({ label, field, options }: { label: string; field: keyof EditableFields; options: { value: string; label: string }[] }) {
    const v = form[field] ?? '';
    if (!editing) {
      return (
        <div className="py-2 grid grid-cols-3 gap-2 text-sm border-b border-gray-100 dark:border-gray-700 last:border-0">
          <span className="text-gray-500">{label}</span>
          <span className="col-span-2 text-gray-800 dark:text-white">
            {options.find((o) => o.value === v)?.label ?? v ?? '—'}
          </span>
        </div>
      );
    }
    return (
      <div className="py-2 grid grid-cols-3 gap-2 items-center text-sm border-b border-gray-100 dark:border-gray-700 last:border-0">
        <label className="text-gray-500">{label}</label>
        <select
          value={v}
          onChange={(e) => setForm({ ...form, [field]: e.target.value })}
          className="col-span-2 px-2 py-1 rounded border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
        >
          <option value="">—</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
    );
  }

  const TABS: { id: ProfileTab; label: string; icon: typeof Users }[] = [
    { id: 'overview', label: 'Overview', icon: IdCard },
    { id: 'teaching', label: 'Teaching', icon: BookOpen },
    { id: 'performance', label: 'Performance', icon: BarChart3 },
    { id: 'admin', label: 'Administrative', icon: Briefcase },
    ...((permissions.canAccessHR || permissions.canAccessPayroll)
      ? [{ id: 'hrpay' as ProfileTab, label: 'HR & Pay', icon: Wallet }]
      : []),
  ];

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 mb-4 hover:text-gray-700">
        <ArrowLeft size={14} /> Back
      </button>

      {/* Header */}
      <Card className="p-5 mb-5">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 text-primary flex items-center justify-center font-bold text-xl shrink-0">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-bold text-lg text-gray-800 dark:text-white">{teacher.displayName}</p>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500 mt-1">
              {teacher.department && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                  <Building2 size={13} /> {teacher.department}
                </span>
              )}
              {teacher.email && (
                <span className="inline-flex items-center gap-1">
                  <Mail size={12} /> {teacher.email}
                </span>
              )}
              {teacher.phone && (
                <span className="inline-flex items-center gap-1">
                  <Phone size={12} /> {teacher.phone}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
              <span><strong className="text-gray-700 dark:text-gray-300">{taughtSubjects.length}</strong> subject{taughtSubjects.length === 1 ? '' : 's'}</span>
              <span>·</span>
              <span><strong className="text-gray-700 dark:text-gray-300">{teacherClassIds.length}</strong> class{teacherClassIds.length === 1 ? '' : 'es'}</span>
              <span>·</span>
              <span><strong className="text-gray-700 dark:text-gray-300">{studentCount}</strong> student{studentCount === 1 ? '' : 's'}</span>
              {teacher.classTeacherOf && (
                <>
                  <span>·</span>
                  <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
                    <GraduationCap size={12} /> Class teacher of {classObj?.name ?? teacher.classTeacherOf}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Tab strip */}
      <Card className="p-1.5 mb-5">
        <div className="flex gap-1 overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl transition-all whitespace-nowrap ${
                  active
                    ? 'bg-primary text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
                }`}
              >
                <Icon size={16} />
                {t.label}
              </button>
            );
          })}
        </div>
      </Card>

      {/* Status banner */}
      {msg && (
        <div className={`mb-4 flex items-center gap-2 text-sm rounded-xl px-3 py-2.5 border ${
          msg.ok
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300'
            : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300'
        }`}>
          {msg.ok ? <Check size={15} /> : <AlertCircle size={15} />} {msg.text}
        </div>
      )}

      {/* ── TAB: OVERVIEW ── */}
      {tab === 'overview' && (
        <div className="space-y-4">
          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm text-gray-900 dark:text-white">Personal Information</h3>
              {canEdit && !editing && (
                <button onClick={() => setEditing(true)} className="flex items-center gap-1 text-xs text-primary hover:underline">
                  <Pencil size={12} /> Edit
                </button>
              )}
              {editing && (
                <div className="flex items-center gap-2">
                  <button onClick={() => setEditing(false)} disabled={saving} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
                    <X size={12} /> Cancel
                  </button>
                  <button onClick={saveOverviewOrAdmin} disabled={saving} className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-primary text-white font-semibold hover:bg-primary/90 disabled:opacity-50">
                    <Save size={12} /> {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              )}
            </div>
            <SelectField label="Title" field="title" options={[
              { value: 'Mr', label: 'Mr' }, { value: 'Mrs', label: 'Mrs' },
              { value: 'Ms', label: 'Ms' }, { value: 'Miss', label: 'Miss' },
              { value: 'Dr', label: 'Dr' },
              { value: 'Mma', label: 'Mma' }, { value: 'Rre', label: 'Rre' },
            ]} />
            <Field label="Initials" field="initials" placeholder="e.g. JK" />
            <Field label="Last Name" field="lastName" placeholder="Surname" />
            <Field label="Email" field="email" type="email" readOnly hint="Sign-in identity — change via User Management → Update Email (System Admin)." />
            <Field label="Phone" field="phone" placeholder="+267 7X XXX XXX" />
            <Field label="Department" field="department" placeholder="e.g. Mathematics" />
            <Field label="Qualifications" field="qualifications" placeholder="e.g. B.Ed, M.Sc" />
          </Card>
        </div>
      )}

      {/* ── TAB: TEACHING (editable) ── */}
      {tab === 'teaching' && (
        <div className="space-y-4">
          {/* Class teacher / assistant assignment */}
          <Card className="p-5">
            <h3 className="font-semibold text-sm text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <GraduationCap size={16} className="text-primary" />
              Class-teacher role
            </h3>
            {!canEdit ? (
              <p className="text-sm text-gray-500">
                {teacher.classTeacherOf
                  ? `Class teacher of ${classObj?.name ?? teacher.classTeacherOf}.`
                  : 'Not a class teacher.'}
              </p>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Primary class teacher of
                  </label>
                  <select
                    value={teacher.classTeacherOf ?? ''}
                    onChange={(e) => handleClassTeacherChange(e.target.value)}
                    disabled={saving}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800 text-sm focus:outline-none focus:border-primary"
                  >
                    <option value="">— Not a class teacher —</option>
                    {classes.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                        {c.classTeacherId && c.classTeacherId !== teacher.id
                          ? ` (currently ${teachers.find((t) => t.id === c.classTeacherId)?.displayName ?? '?'})`
                          : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-gray-400 mt-1">
                    Each teacher can be primary class teacher of one class. Selecting a class currently held by another teacher will reassign it.
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Assistant class teacher of
                  </label>
                  <p className="text-[11px] text-gray-400 mb-2">
                    A teacher can be assistant of multiple classes. Tap a class to toggle.
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {classes.map((c) => {
                      const isAssistantHere = c.assistantClassTeacherId === teacher.id;
                      const hasOtherAssistant = !!(c.assistantClassTeacherId && c.assistantClassTeacherId !== teacher.id);
                      return (
                        <button
                          key={c.id}
                          onClick={() => toggleAssistantOf(c.id)}
                          disabled={saving || hasOtherAssistant}
                          title={hasOtherAssistant ? `Currently held by ${teachers.find((t) => t.id === c.assistantClassTeacherId)?.displayName ?? '?'}` : ''}
                          className={`px-2.5 py-1 rounded-full text-xs border transition-all ${
                            isAssistantHere
                              ? 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700'
                              : hasOtherAssistant
                                ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed dark:bg-gray-800 dark:text-gray-600'
                                : 'bg-white text-gray-600 border-gray-300 hover:border-primary hover:text-primary dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700'
                          }`}
                        >
                          {c.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </Card>

          {/* Subject × class assignment matrix */}
          <Card className="p-5">
            <h3 className="font-semibold text-sm text-gray-900 dark:text-white mb-1 flex items-center gap-2">
              <BookOpen size={16} className="text-primary" />
              Teaching assignments
            </h3>
            <p className="text-xs text-gray-500 mb-3">
              For each subject, tick every class this teacher teaches it to. This is what links the teacher to their students for marks, comments and reports.
            </p>
            {!canEdit ? (
              taughtSubjects.length === 0 ? (
                <p className="text-sm text-gray-400">No teaching assignments recorded.</p>
              ) : (
                <div className="space-y-2">
                  {taughtSubjects.map((s) => (
                    <div key={s.subjectId} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 dark:border-gray-700">
                      <div>
                        <p className="text-sm font-medium text-gray-800 dark:text-white">{s.subjectName}</p>
                        <p className="text-xs text-gray-400">{s.classNames.join(', ')}</p>
                      </div>
                      <span className="text-xs text-gray-400">{s.classNames.length} class{s.classNames.length !== 1 ? 'es' : ''}</span>
                    </div>
                  ))}
                </div>
              )
            ) : (
              <div className="space-y-2 max-h-[28rem] overflow-y-auto pr-1">
                {subjects.length === 0 && (
                  <p className="text-xs text-gray-400">No subjects defined. Add subjects in Setup → Subjects first.</p>
                )}
                {[...subjects].sort((a, b) => a.name.localeCompare(b.name)).map((sub) => {
                  // Effective state = what the resolver actually links, so a
                  // FORM-LEVEL assignment shows its classes as ticked (and is
                  // therefore removable), not just explicit classAssignments.
                  const effectiveCount = classes.filter((c) =>
                    teacherTeachesSubjectInClass(teacher, sub.id, c.id, c.name),
                  ).length;
                  return (
                    <div key={sub.id} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                      <p className="text-sm font-medium mb-1.5">
                        {sub.name}
                        {effectiveCount > 0 && (
                          <span className="ml-2 text-xs text-primary font-normal">
                            {effectiveCount} class{effectiveCount === 1 ? '' : 'es'}
                          </span>
                        )}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {classes.map((c) => {
                          const on = teacherTeachesSubjectInClass(teacher, sub.id, c.id, c.name);
                          return (
                            <button
                              key={c.id}
                              onClick={() => toggleSubjectClass(sub.id, c.id)}
                              className={`px-2.5 py-1 rounded-full text-xs border transition-all ${
                                on
                                  ? 'bg-primary text-white border-primary'
                                  : 'bg-white text-gray-600 border-gray-300 hover:border-primary dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700'
                              }`}
                            >
                              {c.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Class-teacher: list of students */}
          {teacher.classTeacherOf && classStudents.length > 0 && (
            <Card className="p-5">
              <h3 className="font-semibold text-sm text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <Users size={16} className="text-primary" />
                Students in {classObj?.name} ({classStudents.length})
              </h3>
              <div className="grid sm:grid-cols-2 gap-2">
                {classStudents.map((s) => (
                  <div key={s.id} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-100 dark:border-gray-700 text-sm">
                    <GraduationCap size={14} className="text-gray-400 shrink-0" />
                    <span className="text-gray-800 dark:text-white truncate">{s.fullName}</span>
                    <span className="text-xs text-gray-400 ml-auto">{s.admissionNumber ?? ''}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* ── TAB: PERFORMANCE (lazy) ── */}
      {tab === 'performance' && (
        <div className="space-y-4">
          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm text-gray-900 dark:text-white flex items-center gap-2">
                <BarChart3 size={16} className="text-primary" />
                Performance — {term.replace('Term', 'Term ')} {year}
              </h3>
            </div>
            {perfLoading && (
              <p className="text-sm text-gray-400 text-center py-4">Loading performance data…</p>
            )}
            {!perfLoading && perfStats.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-4">
                No marks data available for this term yet.
              </p>
            )}
            {!perfLoading && perfStats.length > 0 && (
              <div className="space-y-2">
                {perfStats.map((s, idx) => (
                  <div key={`${s.subjectName}-${s.className}-${idx}`} className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 dark:text-white truncate">
                        {s.subjectName}
                      </p>
                      <p className="text-xs text-gray-400">
                        {s.className} · {s.studentsGraded} student{s.studentsGraded === 1 ? '' : 's'} graded
                      </p>
                    </div>
                    <div className="relative w-32 h-6 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden shrink-0">
                      {s.passRate !== null && (
                        <div
                          className={`absolute inset-y-0 left-0 ${
                            s.passRate >= 80 ? 'bg-emerald-500' : s.passRate >= 50 ? 'bg-amber-400' : 'bg-red-400'
                          }`}
                          style={{ width: `${s.passRate}%` }}
                        />
                      )}
                      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-gray-700 dark:text-gray-200">
                        {s.passRate !== null ? `${s.passRate}%` : '—'}
                      </span>
                    </div>
                    <div className="text-right w-16 shrink-0">
                      <p className="text-sm font-bold text-gray-800 dark:text-white">{s.average}</p>
                      <p className="text-[10px] text-gray-400">{s.highest}↑ {s.lowest}↓</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ── TAB: ADMINISTRATIVE ── */}
      {tab === 'admin' && (
        <div className="space-y-4">
          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm text-gray-900 dark:text-white">Employment</h3>
              {canEdit && !editing && (
                <button onClick={() => setEditing(true)} className="flex items-center gap-1 text-xs text-primary hover:underline">
                  <Pencil size={12} /> Edit
                </button>
              )}
              {editing && (
                <div className="flex items-center gap-2">
                  <button onClick={() => setEditing(false)} disabled={saving} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
                    <X size={12} /> Cancel
                  </button>
                  <button onClick={saveOverviewOrAdmin} disabled={saving} className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-primary text-white font-semibold hover:bg-primary/90 disabled:opacity-50">
                    <Save size={12} /> {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              )}
            </div>
            <Field label="Staff ID" field="staffId" placeholder="e.g. T-2024-001" />
            <SelectField label="Employment status" field="employmentStatus" options={[
              { value: 'full-time', label: 'Full-time' },
              { value: 'part-time', label: 'Part-time' },
              { value: 'contract', label: 'Contract' },
              { value: 'intern', label: 'Intern' },
            ]} />
            <Field label="Date joined" field="dateJoined" type="date" />
            <Field label="Notes" field="notes" placeholder="Additional notes" />
          </Card>
        </div>
      )}
      {tab === 'hrpay' && (
        <div className="mt-4">
          <TeacherHrPay
            teacherId={teacher.id}
            teacherEmail={teacher.email}
            teacherName={`${teacher.title ?? ''} ${teacher.initials ?? ''} ${teacher.lastName ?? ''}`.trim()}
          />
        </div>
      )}
      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => setSaveModal({ ...saveModal, show: false })}
      />
    </div>
  );
}


